// The signed-in account (if any). Guests are fully supported; signing up later keeps their progress.
// When the server cannot check the session (an outage), the provider keeps what it last knew and sets `unavailable`:
// the learner is never signed out, and Premium never disappears, because the database did not answer.

import type { ReactNode } from 'react'
import type { Access } from '@/lib/plans'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { GUEST_ACCESS } from '@/lib/plans'
import { browserTimeZone } from '@/lib/days'

export type Role = 'learner' | 'editor' | 'reviewer' | 'admin'

export interface SessionUser {
    id: string
    email: string
    displayName: string
    role: Role
    mode: 'kids' | 'adults'
    explanationLang: 'hi' | 'en' | 'both'
    showTranslit: boolean
    leaderboardEnabled: boolean
    emailVerified: boolean
    timeZone: string | null
}

interface SessionContextValue {
    user: SessionUser | null
    /** What this visitor may open; the free part until the session has loaded. */
    access: Access
    loading: boolean
    /** The account could not be checked just now (the server answered "unavailable" or could not be reached). */
    unavailable: boolean
    refresh: () => Promise<SessionUser | null>
    signOut: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

const RETRY_MS = 20000

export const SessionProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [user, setUser] = useState<SessionUser | null>(null)
    const [access, setAccess] = useState<Access>(GUEST_ACCESS)
    const [loading, setLoading] = useState(true)
    const [unavailable, setUnavailable] = useState(false)
    const userRef = useRef<SessionUser | null>(null)
    const retry = useRef<number | null>(null)
    const zoneSaved = useRef<string | null>(null)

    const refresh = useCallback(async (): Promise<SessionUser | null> => {
        if (retry.current) {
            window.clearTimeout(retry.current)
            retry.current = null
        }

        try {
            const res = await fetch('/api/auth/me', { cache: 'no-store' })

            if (res.status === 503 || res.status >= 500) {
                // Keep the last known account and access; try again shortly.
                setUnavailable(true)
                retry.current = window.setTimeout(() => { void refresh() }, RETRY_MS)

                return userRef.current
            }

            const data = res.ok ? await res.json() as { user: SessionUser | null, access?: Access } : { user: null }

            userRef.current = data.user
            setUser(data.user)
            setAccess(data.access ?? GUEST_ACCESS)
            setUnavailable(Boolean(data.access?.unavailable))

            if (data.access?.unavailable) retry.current = window.setTimeout(() => { void refresh() }, RETRY_MS)

            return data.user
        }
        catch {
            // Offline or the server could not be reached: keep what we knew.
            setUnavailable(true)
            retry.current = window.setTimeout(() => { void refresh() }, RETRY_MS)

            return userRef.current
        }
        finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        refresh()

        const onOnline = () => { void refresh() }

        window.addEventListener('online', onOnline)

        return () => {
            window.removeEventListener('online', onOnline)

            if (retry.current) window.clearTimeout(retry.current)
        }
    }, [refresh])

    // Streaks and XP days follow the learner's own midnight: the account keeps the browser's time zone.
    useEffect(() => {
        if (!user) return

        const zone = browserTimeZone()

        if (user.timeZone === zone || zoneSaved.current === `${user.id}|${zone}`) return

        zoneSaved.current = `${user.id}|${zone}`

        fetch('/api/me/prefs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ timeZone: zone })
        }).catch(() => undefined)
    }, [user])

    const signOut = useCallback(async () => {
        await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined)
        userRef.current = null
        setUser(null)
        setAccess(GUEST_ACCESS)
        setUnavailable(false)
    }, [])

    const value = useMemo(() => ({ user, access, loading, unavailable, refresh, signOut }), [user, access, loading, unavailable, refresh, signOut])

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export const useSession = (): SessionContextValue => {
    const ctx = useContext(SessionContext)

    if (!ctx) throw new Error('useSession must be used inside SessionProvider')

    return ctx
}

export const canReview = (user: SessionUser | null): boolean => user?.role === 'reviewer' || user?.role === 'admin'
export const canEdit = (user: SessionUser | null): boolean => canReview(user) || user?.role === 'editor'
